import * as THREE from 'three';
import { resolveLevelBounds, levelZRange, loadFloorFootprint, hasGlobalPropertyService } from '@pointorama/potree-shared';
import type { ResolvedLevelBounds } from '@pointorama/potree-shared';
import { DetectedFloorPlane } from '../floorDetection';
import { ClipTask } from '../../defines';
import type { IsolationClipFragment, ClipBoxData, ClipPlaneData } from '../../viewer/buildClipState';

// Floor-isolation strategies, per-view specs, and the pure geometry that turns
// a strategy into a clip fragment. Isolation is applied per RenderView by
// `FloorIsolationManager` (no scene objects) — there is no controller class.

export const DEFAULT_FLOOR_ISOLATION_HEIGHT = 2.1;

export type FloorIsolationStrategy =
  | { kind: 'fixedHeight'; floorGroupId: string; height: number }
  | { kind: 'roomBounds'; floorGroupId: string; floorHeight: number; ceilingHeight: number; margin?: number }
  | { kind: 'box'; xyCorners: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2]; minZ: number; maxZ: number };

export const defaultFloorIsolationStrategy = (floorGroupId: string): FloorIsolationStrategy => ({
  kind: 'fixedHeight',
  floorGroupId,
  height: DEFAULT_FLOOR_ISOLATION_HEIGHT,
});

/**
 * A per-view isolation request: a `base` strategy applied to every view, plus
 * optional `overrides` keyed by view-mode id. A key absent ⇒ that view uses
 * `base`; a key mapped to `null` ⇒ that view is exempt (renders the full cloud).
 * X-Ray is the common override case but any view id works (`xrayException`).
 */
export interface IsolationSpec {
  base: FloorIsolationStrategy | null;
  overrides?: Record<string, FloorIsolationStrategy | null>;
}

/** View-mode ids that render in X-Ray density (mirrors RenderViewState.STATIC_XRAY_VIEW_IDS). */
export const XRAY_VIEW_IDS = ['xray_2d', 'xray_2d_surface', 'xray_2d_wall'];

/** Build an `overrides` map applying `strategy` (or `null` = exempt) to every X-Ray view. */
export function xrayException(strategy: FloorIsolationStrategy | null): Record<string, FloorIsolationStrategy | null> {
  return Object.fromEntries(XRAY_VIEW_IDS.map((id) => [id, strategy]));
}

/**
 * Build `ResolvedLevelBounds` from in-hand floor/ceiling planes when the full
 * level list isn't available (group not persisted yet, manager not seeded).
 * Without a real level list, `nextFloorZ` is unknowable — padding kinds that
 * need it fall back to their local defaults.
 */
export function boundsFromPlanes(floorGroupId: string, floor: DetectedFloorPlane, ceiling: DetectedFloorPlane | null): ResolvedLevelBounds {
  const bounds = resolveLevelBounds([{ groupIdentifier: floorGroupId, floorZ: floor.height, ceilingZ: ceiling?.height ?? null }], floorGroupId);
  if (!bounds) {
    throw new Error('boundsFromPlanes: single-level resolve cannot fail');
  }
  return bounds;
}

/** A floor group's persisted footprint corners as Vector2s, or null when not stored. */
export function loadFloorFootprintCorners(floorGroupId: string): [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2] | null {
  if (!hasGlobalPropertyService()) return null;
  const corners = loadFloorFootprint(floorGroupId);
  if (!corners) return null;
  return [new THREE.Vector2(corners[0].x, corners[0].y), new THREE.Vector2(corners[1].x, corners[1].y), new THREE.Vector2(corners[2].x, corners[2].y), new THREE.Vector2(corners[3].x, corners[3].y)];
}

/**
 * Full-floor slab with `'slabSurface'` padding: the floor/ceiling planes are
 * fitted THROUGH the slab's point scatter, so the exact interior range would
 * cut roughly half the surface points and make the floor look sparse. Under
 * true-ceiling semantics the inter-storey slab itself is intentionally
 * excluded here — X-ray shows the storey interior. With stored footprint
 * corners the clip is the floor's OWN box; without them (legacy/manual
 * floors) it degrades to the XY-infinite z-slab.
 */
export function fullFloorRoomBounds(bounds: ResolvedLevelBounds, footprintCorners?: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2] | null): FloorIsolationStrategy {
  const { minZ, maxZ } = levelZRange(bounds, 'slabSurface');
  if (footprintCorners) {
    return { kind: 'box', xyCorners: footprintCorners, minZ, maxZ };
  }
  return { kind: 'roomBounds', floorGroupId: bounds.groupIdentifier, floorHeight: minZ, ceilingHeight: maxZ };
}

/** Default per-view spec: fixed-height in 3D, full-floor slab/box in X-Ray. */
export function defaultIsolationSpec(bounds: ResolvedLevelBounds, footprintCorners?: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2] | null): IsolationSpec {
  return { base: defaultFloorIsolationStrategy(bounds.groupIdentifier), overrides: xrayException(fullFloorRoomBounds(bounds, footprintCorners)) };
}

function resolvePlaneBounds(strategy: Extract<FloorIsolationStrategy, { kind: 'fixedHeight' | 'roomBounds' }>, floor: DetectedFloorPlane): { centerZ: number; tolerance: number } {
  switch (strategy.kind) {
    case 'fixedHeight': {
      const height = strategy.height;
      return { centerZ: floor.height + height / 2, tolerance: height / 2 };
    }
    case 'roomBounds': {
      const margin = strategy.margin ?? 0;
      const low = strategy.floorHeight - margin;
      const high = strategy.ceilingHeight + margin;
      return { centerZ: (low + high) / 2, tolerance: (high - low) / 2 };
    }
  }
}

/** Stable id for the floor-isolation clip (shared across views; one per view's clip array). */
export const FLOOR_ISOLATION_CLIP_ID = 'floor-isolation';

/**
 * Resolve a strategy to a per-view clip fragment WITHOUT building scene objects
 * (no `PointoramaPlane`/`BoxVolume` → no reactive props, no geometry, no
 * dispose). The shader only reads the scalar fields below plus the precomputed
 * `inverse` matrix; the matrix is composed directly to match what the real
 * objects' `matrixWorld` would produce. Returns null when a plane strategy has
 * no floor data.
 */
export function resolveStrategyToClipFragment(strategy: FloorIsolationStrategy, floor: DetectedFloorPlane | null, _ceiling: DetectedFloorPlane | null): IsolationClipFragment | null {
  if (strategy.kind === 'box') {
    return { clipBoxes: [buildBoxFragment(strategy)], clipPlanes: [] };
  }
  if (!floor) return null;
  return { clipBoxes: [], clipPlanes: [buildPlaneFragment(strategy, floor)] };
}

// The shim objects below are bare `THREE.Object3D`s (no geometry, no meshes, no
// reactive props) carrying the exact fields BOTH render paths read: the shader
// path (PotreeRenderer/PointCloudMaterial — scalar fields + the precomputed
// `inverse`) AND the LOD/visibility pass (Potree_update_visibility — needs
// `matrixWorld`, and for planes `getNormal()`/`position`/`tolerance`/
// `pointInsideClipArea()`). Object3D gives correct `position`/`matrixWorld` for
// free; we attach the rest. Missing any of these throws in the per-frame LOD
// pass — which is what froze the renderer the first time around.

function buildPlaneFragment(strategy: Extract<FloorIsolationStrategy, { kind: 'fixedHeight' | 'roomBounds' }>, floor: DetectedFloorPlane): ClipPlaneData {
  const { centerZ, tolerance } = resolvePlaneBounds(strategy, floor);
  const normal = new THREE.Vector3(0, 0, 1);

  // Normal (0,0,1) ⇒ identity rotation ⇒ matrixWorld is pure translation,
  // matching `PointoramaPlane.setRotationFromNormal((0,0,1))`.
  const obj = new THREE.Object3D();
  obj.position.set(floor.center.x, floor.center.y, centerZ);
  obj.updateMatrixWorld(true);
  Object.assign(obj, {
    actualClipTask: ClipTask.SHOW_INSIDE,
    tolerance,
    gradient: 'SPECTRAL',
    heatmapIsolate: false,
    useIntersection: false,
    identifier: FLOOR_ISOLATION_CLIP_ID,
    getNormal: () => normal.clone(),
    // Infinite horizontal slab — always "inside" in XY (covers the cloud);
    // the Z slab is enforced by the shader / tolerance.
    pointInsideClipArea: () => true,
  });

  return { plane: obj as unknown as ClipPlaneData['plane'], inverse: obj.matrixWorld.clone().invert(), position: obj.position.clone() };
}

function buildBoxFragment(strategy: Extract<FloorIsolationStrategy, { kind: 'box' }>): ClipBoxData {
  // Same transform a BoxVolume at this position/rotation/scale would produce.
  const corners = strategy.xyCorners;
  const edgeX = new THREE.Vector2().subVectors(corners[1], corners[0]);
  const edgeY = new THREE.Vector2().subVectors(corners[3], corners[0]);
  const sizeX = edgeX.length();
  const sizeY = edgeY.length();
  const sizeZ = strategy.maxZ - strategy.minZ;
  const centerXY = new THREE.Vector2().addVectors(corners[0], corners[2]).multiplyScalar(0.5);
  const centerZ = (strategy.minZ + strategy.maxZ) / 2;
  const angle = Math.atan2(edgeX.y, edgeX.x);

  const obj = new THREE.Object3D();
  obj.position.set(centerXY.x, centerXY.y, centerZ);
  obj.rotation.set(0, 0, angle);
  obj.scale.set(sizeX, sizeY, sizeZ);
  obj.updateMatrixWorld(true);
  Object.assign(obj, {
    getActualClipTask: () => ClipTask.SHOW_INSIDE,
    color: new THREE.Color(0xffffff),
    useIntersection: true,
  });

  return { box: obj as unknown as ClipBoxData['box'], inverse: obj.matrixWorld.clone().invert(), position: obj.position.clone() };
}
